<script lang="ts">
  /**
   * La fiche société — l'écran où tout se décide, au pouce.
   *
   * Chaque tap écrit immédiatement (état local optimiste + file d'écriture) :
   * il n'y a pas de bouton « Enregistrer », donc rien à oublier de valider si
   * l'app est fermée au milieu.
   *
   * L'ordre des blocs suit celui dans lequel les décisions se prennent :
   * le rôle commande le reste (une société classée bruit n'a ni profil
   * d'achat ni cadence de relance), puis le groupe, puis ce qui est
   * facultatif — étiquettes et organigramme.
   */

  import { onDestroy, untrack } from "svelte";
  import {
    crmState,
    companyByUid,
    setCountry,
    setNotes,
    setTier,
    setBuyerProfile,
    toggleClassification,
    markNoise,
    toggleTag,
    toggleFunction,
    setArchived,
    setReviewed,
    deleteCompany,
    undoDeleteCompany,
    linksOf,
    tagUidsOf,
    completenessOf,
  } from "../crm/store.svelte";
  import { isNoise, classificationOf, SLOT_LABELS } from "../crm/completeness";
  import {
    BUYER_PROFILE_LABELS,
    CLASSIFICATION_LABELS,
    FUNCTION_LABELS,
    contactName,
    label,
    type Company,
  } from "../crm/types";
  import {
    COUNTRIES_SORTED,
    FREQUENT_COUNTRIES,
    country as countryOf,
  } from "../crm/countries";
  import CrmPillRow, { type PillItem } from "./CrmPillRow.svelte";
  import Icon from "./shared/Icon.svelte";
  import { showToast } from "../snackbar/store.svelte";
  import { haptic } from "../gestures/haptic";

  interface Props {
    company: Company;
    /** Passe à la fiche suivante de la file. Absent = fin de file. */
    onNext: (() => void) | null;
    onClose: () => void;
  }

  let { company, onNext, onClose }: Props = $props();

  const cls = $derived(classificationOf(company));
  const noise = $derived(isNoise(company));
  const comp = $derived(completenessOf(company));
  const tagUids = $derived(tagUidsOf(company.uid));

  const rolePills = $derived<PillItem[]>(
    crmState.vocab.classifications.map((v) => ({
      value: v,
      label: label(CLASSIFICATION_LABELS, v),
    })),
  );

  const profilePills = $derived<PillItem[]>(
    crmState.vocab.buyerProfiles.map((v) => ({
      value: v,
      label: label(BUYER_PROFILE_LABELS, v),
    })),
  );

  const tierPills = $derived<PillItem[]>(
    crmState.vocab.tiers.map((t) => ({
      value: t.code,
      label: t.label,
      hint: t.cadence_days ? `${t.cadence_days} j` : "—",
    })),
  );

  const tagPills = $derived<PillItem[]>(
    crmState.tags.map((t) => ({ value: t.uid, label: t.name, color: t.color })),
  );

  /**
   * Les huit pays qui font l'essentiel du portefeuille sont en accès direct ;
   * le reste passe par le sélecteur natif. Un pays déjà posé mais hors de
   * cette liste s'ajoute en tête, sinon il serait invisible et impossible à
   * retirer d'un tap.
   */
  const countryPills = $derived<PillItem[]>(
    (company.country_code && !FREQUENT_COUNTRIES.includes(company.country_code)
      ? [company.country_code, ...FREQUENT_COUNTRIES]
      : FREQUENT_COUNTRIES
    ).map((code) => {
      const c = countryOf(code);
      return { value: code, label: c ? `${c.flag} ${c.name}` : code };
    }),
  );

  const functionPills = $derived<PillItem[]>(
    crmState.vocab.functions.map((v) => ({
      value: v,
      label: label(FUNCTION_LABELS, v),
    })),
  );

  /** Le primaire d'abord, puis par nom — l'organigramme se lit de haut en bas. */
  const contacts = $derived(
    [...linksOf(company.uid)]
      .filter((l) => l.contact)
      .sort(
        (a, b) =>
          Number(b.is_primary) - Number(a.is_primary) ||
          contactName(a.contact!).localeCompare(contactName(b.contact!), "fr"),
      ),
  );

  const money = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1,
  });

  const facts = $derived(
    [
      company.naf_label,
      company.revenue ? `CA ${money.format(company.revenue)}` : null,
      company.employees ? `${company.employees} sal.` : null,
    ].filter(Boolean) as string[],
  );

  // === Note libre =========================================================
  // La frappe ne part pas caractère par caractère : un brouillon local, une
  // écriture temporisée, et un envoi forcé dès qu'on quitte le champ, qu'on
  // change de fiche ou qu'on démonte. Sans ce dernier point, la dernière
  // phrase tapée avant un « Suivant » serait perdue.
  const NOTE_DEBOUNCE_MS = 800;
  let noteDraft = $state("");
  let draftUid: string | null = null;
  let noteTimer: ReturnType<typeof setTimeout> | null = null;

  function commitNotes() {
    if (noteTimer) {
      clearTimeout(noteTimer);
      noteTimer = null;
    }
    if (draftUid === null) return;
    const c = companyByUid(draftUid);
    if (c && (c.notes ?? "") !== noteDraft.trim()) setNotes(draftUid, noteDraft);
  }

  function onNoteInput() {
    if (noteTimer) clearTimeout(noteTimer);
    noteTimer = setTimeout(commitNotes, NOTE_DEBOUNCE_MS);
  }

  // `untrack` sur la lecture de `notes` : sans lui, notre propre écriture
  // relancerait l'effet et réinitialiserait le champ en pleine frappe.
  $effect(() => {
    const uid = company.uid;
    if (uid === draftUid) return;
    commitNotes();
    draftUid = uid;
    noteDraft = untrack(() => company.notes ?? "");
  });

  onDestroy(commitNotes);

  const reviewed = $derived(!!company.reviewed_at);

  /**
   * « Terminer » ne veut pas dire « tout est rempli », mais « je n'ai plus
   * rien à saisir ici ». C'est la sortie de secours des fiches que la règle
   * de complétude ne saura jamais satisfaire — un partenaire n'aura pas
   * d'étiquette métier, une administration pas de cadence de relance.
   */
  function finish() {
    commitNotes();
    haptic("medium");
    if (reviewed) {
      // Déjà classée : on annule, et on RESTE sur la fiche pour que le
      // changement soit visible.
      setReviewed(company.uid, false);
      return;
    }
    setReviewed(company.uid, true);
    if (onNext) onNext();
    else onClose();
  }

  function goNext() {
    commitNotes();
    onNext?.();
  }

  function goClose() {
    commitNotes();
    onClose();
  }

  function onRole(value: string) {
    // « Bruit » remplace le rôle au lieu de s'y ajouter : une société qui est
    // du bruit n'est pas aussi un client.
    if (value === "noise" && !noise) markNoise(company.uid);
    else toggleClassification(company.uid, value);
  }

  function onArchive() {
    haptic("heavy");
    const next = !company.archived;
    setArchived(company.uid, next);
    showToast({ message: next ? "Archivée" : "Désarchivée" });
  }

  function onDelete() {
    if (
      !confirm(
        `Supprimer « ${company.name} » ?\n\nLa suppression remontera au Mac et y effacera la fiche et ses liens contacts.`,
      )
    ) {
      return;
    }
    haptic("heavy");
    const snapshot = $state.snapshot(company) as Company;
    deleteCompany(company.uid);
    onClose();
    showToast({
      message: `« ${snapshot.name} » supprimée`,
      action: { label: "Annuler", run: () => undoDeleteCompany(snapshot) },
      duration: 8000,
    });
  }
</script>

<article class="sheet">
  <header class="hero">
    <h1>{company.name}</h1>
    <p class="sub">
      {[company.domain, company.city, company.country_code]
        .filter(Boolean)
        .join(" · ") || "—"}
    </p>
    {#if facts.length > 0}
      <p class="facts">{facts.join(" · ")}</p>
    {/if}

    {#if reviewed}
      <p class="reviewed">
        <Icon name="check" size={12} />
        Classée — retirée des files
      </p>
    {/if}

    <ul class="checklist" aria-label="Complétude">
      {#each comp.relevant as slot (slot)}
        <li
          class:on={comp.filled[slot]}
          class:required={comp.required.includes(slot)}
        >
          <span class="dot" aria-hidden="true"></span>
          {SLOT_LABELS[slot]}
        </li>
      {/each}
    </ul>
  </header>

  <section>
    <h3>Rôle</h3>
    <CrmPillRow items={rolePills} selected={cls} onToggle={onRole} danger="noise" />
  </section>

  <!-- Le profil décrit un MODE D'ACHAT : hors client et négociant, la
       question ne se pose pas et la section disparaît plutôt que de
       proposer une case vide à ne jamais remplir. -->
  {#if comp.relevant.includes("profile")}
    <section>
      <h3>Profil d'achat</h3>
      <CrmPillRow
        items={profilePills}
        selected={company.buyer_profile ? [company.buyer_profile] : []}
        onToggle={(v) => setBuyerProfile(company.uid, v)}
      />
    </section>
  {/if}

  {#if !noise}
    <section>
      <h3>Groupe</h3>
      <CrmPillRow
        items={tierPills}
        selected={company.tier ? [company.tier] : []}
        onToggle={(v) => setTier(company.uid, v)}
      />
    </section>

    <section>
      <h3>Pays</h3>
      <CrmPillRow
        items={countryPills}
        selected={company.country_code ? [company.country_code] : []}
        onToggle={(v) =>
          setCountry(company.uid, company.country_code === v ? null : v)}
      />
      <select
        class="country-select"
        value={company.country_code ?? ""}
        onchange={(e) =>
          setCountry(company.uid, e.currentTarget.value || null)}
      >
        <option value="">Autre pays…</option>
        {#each COUNTRIES_SORTED as c (c.code)}
          <option value={c.code}>{c.flag} {c.name}</option>
        {/each}
      </select>
    </section>
  {/if}

  <section>
    <h3>Étiquettes</h3>
    {#if tagPills.length === 0}
      <p class="empty">Aucune étiquette définie.</p>
    {:else}
      <CrmPillRow
        items={tagPills}
        selected={tagUids}
        onToggle={(v) => toggleTag(company.uid, v)}
      />
    {/if}
  </section>

  <section>
    <h3>Organigramme <span class="count">{contacts.length}</span></h3>
    {#if contacts.length === 0}
      <p class="empty">Aucun contact rattaché.</p>
    {:else}
      <ul class="org">
        {#each contacts as link (link.contact_uid)}
          <li>
            <div class="who">
              <strong>{contactName(link.contact!)}</strong>
              {#if link.is_primary}<span class="badge">principal</span>{/if}
              <span class="mail">{link.contact!.email}</span>
              {#if link.contact!.role}
                <span class="title">{link.contact!.role}</span>
              {/if}
            </div>
            <CrmPillRow
              items={functionPills}
              selected={link.functions ?? []}
              onToggle={(v) => toggleFunction(company.uid, link.contact_uid, v)}
            />
          </li>
        {/each}
      </ul>
    {/if}
  </section>

  <section>
    <h3>Note</h3>
    <textarea
      class="note"
      rows="4"
      bind:value={noteDraft}
      oninput={onNoteInput}
      onblur={commitNotes}
      placeholder="Ce qu'il faut se rappeler sur cette société…"
    ></textarea>
  </section>

  <section class="zone">
    <button class="ghost" onclick={onArchive}>
      {company.archived ? "Désarchiver" : "Archiver"}
    </button>
    <button class="ghost danger" onclick={onDelete}>Supprimer</button>
  </section>

  <footer>
    <!-- Trois sorties distinctes : quitter la file, passer à la suivante en
         laissant celle-ci dedans, ou la déclarer classée et enchaîner. -->
    <button class="stop" onclick={goClose}>Quitter</button>
    {#if onNext}
      <button class="next" onclick={goNext}>Suivant ›</button>
    {/if}
    <button class="finish" class:undo={reviewed} onclick={finish}>
      <Icon name="check" size={15} />
      {reviewed ? "Classée" : "Terminer"}
    </button>
  </footer>
</article>

<style>
  .sheet {
    padding: 4px 16px 24px;
  }

  /* === En-tête ============================================================ */
  .hero {
    padding: 8px 0 18px;
    border-bottom: 1px solid var(--border-subtle);
  }
  h1 {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    font-size: 31px;
    line-height: 1;
    letter-spacing: -0.015em;
    line-height: 1.1;
    margin: 0 0 6px;
  }
  .sub {
    margin: 0;
    font-size: 13.5px;
    color: var(--fg-muted);
  }
  .facts {
    margin: 4px 0 0;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--fg-subtle);
  }

  .reviewed {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 12px 0 0;
    padding: 3px 9px;
    border-radius: var(--radius-pill);
    background: var(--good-bg);
    color: var(--good);
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .checklist {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    font-size: 11.5px;
    color: var(--fg-subtle);
  }
  .checklist li {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  /* Une case exigée mais vide est la seule à s'afficher en pleine encre :
     c'est ce qui reste à faire, tout le reste est décoratif. */
  .checklist li.required {
    color: var(--accent);
    font-weight: 600;
  }
  .checklist li.on {
    color: var(--fg-subtle);
    font-weight: 400;
  }
  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    border: 1.5px solid currentColor;
  }
  .checklist li.on .dot {
    background: currentColor;
  }

  /* === Blocs ============================================================== */
  section {
    padding: 18px 0;
    border-bottom: 1px solid var(--border-subtle);
  }
  h3 {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin: 0 0 12px;
    /* Libellé de section : Azeret Mono, très petit, majuscules — la
       convention du thème pour tout ce qui étiquette sans être du contenu. */
    font-family: var(--font-mono);
    font-size: 9.5px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .count {
    font-family: var(--font-mono);
    font-weight: 400;
    letter-spacing: 0;
  }
  .empty {
    margin: 0;
    font-size: 13px;
    color: var(--fg-subtle);
  }

  /* === Organigramme ======================================================= */
  .org {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
  .who {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 8px;
  }
  .who strong {
    font-size: 15px;
    font-weight: 600;
  }
  .badge {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 2px 6px;
    border-radius: 4px;
    background: var(--bg-hover);
    color: var(--fg-subtle);
  }
  .mail,
  .title {
    font-size: 12px;
    color: var(--fg-subtle);
  }
  .title {
    font-style: italic;
  }

  /* === Actions ============================================================ */
  .zone {
    display: flex;
    gap: 10px;
    border-bottom: 0;
  }
  .ghost {
    flex: 1;
    height: 42px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--fg-muted);
    font-family: var(--font-sans);
    font-size: 14px;
    cursor: pointer;
  }
  .ghost.danger {
    color: var(--bad);
    border-color: color-mix(in srgb, var(--bad) 35%, transparent);
  }

  .country-select {
    display: block;
    width: 100%;
    margin-top: 10px;
    height: 42px;
    padding: 0 12px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: var(--bg-elev);
    color: var(--fg);
    font-family: var(--font-sans);
    font-size: 16px; /* < 16px déclenche le zoom automatique iOS */
  }

  .note {
    width: 100%;
    padding: 12px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: var(--bg-elev);
    color: var(--fg);
    font-family: var(--font-sans);
    font-size: 16px; /* idem : évite le zoom iOS à la mise au point */
    line-height: 1.5;
    resize: vertical;
  }
  .note:focus {
    outline: none;
    border-color: var(--accent);
  }

  footer {
    display: flex;
    gap: 10px;
    position: sticky;
    bottom: 0;
    padding: 12px 0 max(12px, env(safe-area-inset-bottom));
    background: linear-gradient(to top, var(--bg) 62%, transparent);
  }
  .stop {
    flex-shrink: 0;
    padding: 0 14px;
    height: 50px;
    border: 1px solid var(--border);
    border-radius: 14px;
    background: var(--bg);
    color: var(--fg-muted);
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
  }
  .next {
    flex: 1;
    height: 50px;
    border: 1px solid var(--border);
    border-radius: 14px;
    background: var(--bg);
    color: var(--fg);
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
  }

  /* L'action principale : c'est celle qu'on fait le plus souvent. */
  .finish {
    flex: 1.3;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 50px;
    border: 0;
    border-radius: 14px;
    background: var(--accent);
    color: var(--accent-fg);
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
  }
  .finish.undo {
    background: var(--good-bg);
    color: var(--good);
  }
  .next:active,
  .finish:active {
    transform: scale(0.99);
  }
</style>
